import React from 'react';
import { Pencil, CircleCheck, XCircle } from 'lucide-react';
import {
  Button,
  Tooltip as RadixTooltip,
  TooltipProvider,
  TooltipTrigger,
  TooltipContent,
} from '@cargomatic/ui';
import {
  accessorialCanBeInvoiced,
  accessorialPaid,
  userCanApproveAccessorials as userCanApproveAccessorialsHelper,
  checkIfEquipmentIsMissingForAccessorial,
  checkWhichSideAccessorialAutoDeclined,
  isCarrierMissingInAccessorial,
} from 'helpers/accessorials';
import { isVendorChargePaid } from 'common/helpers/accessorials';
import { isShipmentCanceled } from 'helpers/shipments';
import SessionStore from 'stores/SessionStore';
import USER_TYPES from 'constants/UserTypes';
import { TOOLTIP_CONTENT } from './accessorialTooltips';

function getTooltipContent({
  buttonType,
  isMissingEquipment = false,
  declinedAccessorial = false,
  isCarrierMissing = false,
  isProcessed = false,
  isMissingDocs = false,
}) {
  if (isProcessed) {
    return TOOLTIP_CONTENT.PROCESSED;
  }
  if (declinedAccessorial) {
    return TOOLTIP_CONTENT.SHIPPER_CHANGED;
  }
  if (isCarrierMissing) {
    return TOOLTIP_CONTENT.NO_CARRIER_ASSIGNED_INFO;
  }
  if (isMissingEquipment && buttonType === 'approve') {
    return TOOLTIP_CONTENT.MISSING_EQUIPMENT;
  }
  if (isMissingDocs && buttonType === 'approve') {
    return TOOLTIP_CONTENT.MISSING_DOCS;
  }
  return TOOLTIP_CONTENT.EMPTY_TOOLTIP;
}

function userCanApproveAccessorial(accessorial) {
  const user = SessionStore.getUser();
  return userCanApproveAccessorialsHelper(user, [accessorial]);
}

function checkMissingDocsByUserType(accessorial, type) {
  if (type === USER_TYPES.CARRIER) {
    return !!accessorial?.missing_docs;
  }
  return (
    !!accessorial?.missing_docs ||
    accessorial?.customer_approval_provided === false
  );
}

const WithTooltip = ({ content, children }) => {
  if (!content) {
    return children;
  }
  return (
    <TooltipProvider>
      <RadixTooltip>
        <TooltipTrigger asChild>{children}</TooltipTrigger>
        <TooltipContent side="top">{content}</TooltipContent>
      </RadixTooltip>
    </TooltipProvider>
  );
};

const AccessorialActionButtons = ({
  accessorial,
  accessorialId,
  shipment,
  type,
  isAccessorialsAdmin,
  accessorialFetched,
  isEditSaving,
  isApproveSaving,
  isDeclineSaving,
  isEditable,
  onEdit,
  onApprove,
  onDecline,
}) => {
  const isAnySaving = isEditSaving || isApproveSaving || isDeclineSaving;
  const isVendorPaid =
    accessorial?.carrier_status === 'approved' &&
    isVendorChargePaid(accessorialId, shipment?.vendor_payables) &&
    type === USER_TYPES.CARRIER;
  const shipmentCarrier = shipment?.carrier?._id || shipment?.carrier;
  const carrierAssociatedWithAccessorial =
    accessorial.carrier?._id || accessorial.carrier;
  const carrierMismatch =
    USER_TYPES.CARRIER === type &&
    accessorial.payee_type === 'carrier' &&
    shipmentCarrier !== carrierAssociatedWithAccessorial;

  const isAlreadyApproved =
    type === USER_TYPES.CARRIER
      ? accessorial.carrier_status === 'approved'
      : accessorial.shipper_status === 'approved';
  const isAlreadyDeclined =
    type === USER_TYPES.CARRIER
      ? accessorial.carrier_status === 'declined'
      : accessorial.shipper_status === 'declined';

  const disablePayeeApprove =
    (carrierMismatch && USER_TYPES.CARRIER === type) || isAlreadyApproved;
  const disablePayeeDecline = isAlreadyDeclined;
  const disablePayeeEdit = carrierMismatch && USER_TYPES.CARRIER === type;
  const processCheck =
    type === USER_TYPES.SHIPPER ? accessorialCanBeInvoiced : accessorialPaid;

  const isProcessed = processCheck(shipment, accessorial);
  // After a carrier change, payables tied to the previous carrier still mark
  // the accessorial as "paid" — don't let that gate decline for the new carrier.
  const isProcessedBlocksDecline = isProcessed && !carrierMismatch;
  const isCanceled = isShipmentCanceled(shipment);

  const userCanApprove = userCanApproveAccessorial(accessorial, type);
  const isMissingDocs = checkMissingDocsByUserType(accessorial, type);
  const isMissingEquipment =
    checkIfEquipmentIsMissingForAccessorial(accessorial);
  const declinedAccessorial = checkWhichSideAccessorialAutoDeclined(
    accessorial,
    type,
  );
  const isCarrierMissing =
    type === 'carrier' ? isCarrierMissingInAccessorial(accessorial) : false;

  // Edit disabled logic
  const editDisabled =
    isCanceled ||
    isProcessed ||
    disablePayeeEdit ||
    !accessorialFetched ||
    !isEditable ||
    isVendorPaid ||
    declinedAccessorial ||
    isCarrierMissing ||
    isAnySaving;

  // Approve disabled logic
  let approveDisabled =
    isCanceled ||
    isProcessed ||
    disablePayeeApprove ||
    !accessorialFetched ||
    !userCanApprove ||
    isVendorPaid ||
    isMissingEquipment ||
    declinedAccessorial ||
    isCarrierMissing ||
    isAnySaving;
  if (isMissingDocs && !isAccessorialsAdmin) {
    approveDisabled = true;
  }

  // Decline disabled logic
  const declineDisabled =
    isCanceled ||
    isProcessedBlocksDecline ||
    disablePayeeDecline ||
    isVendorPaid ||
    declinedAccessorial ||
    isCarrierMissing ||
    isAnySaving;

  const editTooltip = getTooltipContent({
    buttonType: 'edit',
    isMissingEquipment,
    declinedAccessorial,
    isCarrierMissing,
    isProcessed,
  });
  const approveTooltip = getTooltipContent({
    buttonType: 'approve',
    isMissingEquipment,
    declinedAccessorial,
    isCarrierMissing,
    isProcessed,
    isMissingDocs,
  });
  const declineTooltip = getTooltipContent({
    buttonType: 'declined',
    isMissingEquipment,
    declinedAccessorial,
    isCarrierMissing,
    isProcessed: isProcessedBlocksDecline,
  });

  return (
    <div data-testid="div-7704ff1512" className="flex justify-end gap-0.5">
      <WithTooltip content={editDisabled ? editTooltip : null}>
        <Button
          data-testid="EditButton-ed69f1e2"
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          disabled={editDisabled}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onEdit(type);
          }}
        >
          <Pencil className="h-4 w-4" />
        </Button>
      </WithTooltip>
      <WithTooltip content={approveDisabled ? approveTooltip : null}>
        <Button
          data-testid="ApproveButton-c6f695d8"
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-green-600 hover:text-green-700 hover:bg-green-50"
          disabled={approveDisabled}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onApprove(type);
          }}
        >
          <CircleCheck className="h-5 w-5" />
        </Button>
      </WithTooltip>
      <WithTooltip content={declineDisabled ? declineTooltip : null}>
        <Button
          data-testid="DeclineButton-09f71576"
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
          disabled={declineDisabled}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onDecline(type);
          }}
        >
          <XCircle className="h-5 w-5" />
        </Button>
      </WithTooltip>
    </div>
  );
};

export default AccessorialActionButtons;
